import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Broom, ArrowRight, CheckCircle, Warning, ArrowsClockwise, Printer, Plus } from '@phosphor-icons/react';
import { printHousekeepingSlip } from '@/lib/printSlip';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function HousekeepingPage({ user }) {
  const [suggestions, setSuggestions] = useState([]);
  const [pendingTasks, setPendingTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);

  const [showMoveModal, setShowMoveModal] = useState(false);
  const [moveFromId, setMoveFromId] = useState('');
  const [moveInventory, setMoveInventory] = useState([]);
  const [moveSelectedIds, setMoveSelectedIds] = useState(new Set());
  const [moveToId, setMoveToId] = useState('');
  const [movePriority, setMovePriority] = useState('normal');
  const [moveReason, setMoveReason] = useState('');
  const [moveLoading, setMoveLoading] = useState(false);
  const [moveSubmitting, setMoveSubmitting] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('warehouse_token');
      const [s, t, l] = await Promise.all([
        axios.get(`${API}/housekeeping/suggestions`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/housekeeping/tasks`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/locations`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setSuggestions(s.data);
      setPendingTasks(t.data);
      setLocations(l.data || []);
    } catch (error) {
      toast.error('Failed to load housekeeping data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSuggestions = fetchAll;

  const openMoveModal = () => {
    setMoveFromId('');
    setMoveInventory([]);
    setMoveSelectedIds(new Set());
    setMoveToId('');
    setMovePriority('normal');
    setMoveReason('');
    setShowMoveModal(true);
  };

  const handleFromLocationChange = async (locId) => {
    setMoveFromId(locId);
    setMoveSelectedIds(new Set());
    setMoveInventory([]);
    if (!locId) return;
    setMoveLoading(true);
    try {
      const token = localStorage.getItem('warehouse_token');
      const res = await axios.get(`${API}/housekeeping/location-inventory/${locId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMoveInventory(res.data.inventory || []);
      if ((res.data.inventory || []).length === 0) {
        toast.info('This location has no stock');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load location stock');
    } finally {
      setMoveLoading(false);
    }
  };

  const toggleMoveSelected = (invId) => {
    const next = new Set(moveSelectedIds);
    if (next.has(invId)) next.delete(invId);
    else next.add(invId);
    setMoveSelectedIds(next);
  };

  const toggleMoveSelectAll = () => {
    if (moveSelectedIds.size === moveInventory.length) {
      setMoveSelectedIds(new Set());
    } else {
      setMoveSelectedIds(new Set(moveInventory.map(r => r.inventory_id)));
    }
  };

  const handleCreateManualMove = async () => {
    if (!moveFromId || !moveToId) {
      toast.error('Please pick both FROM and TO locations');
      return;
    }
    if (moveFromId === moveToId) {
      toast.error('FROM and TO must be different');
      return;
    }
    if (moveSelectedIds.size === 0) {
      toast.error('Tick at least one stock line to move');
      return;
    }
    setMoveSubmitting(true);
    try {
      const token = localStorage.getItem('warehouse_token');
      const res = await axios.post(`${API}/housekeeping/create-task`, {
        from_location_id: moveFromId,
        to_location_id: moveToId,
        inventory_ids: Array.from(moveSelectedIds),
        priority: movePriority,
        reason: moveReason || 'Manual stock move',
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Assigned — slip ${res.data.slip_no} generated`);
      printHousekeepingSlip(res.data);
      setShowMoveModal(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create move');
    } finally {
      setMoveSubmitting(false);
    }
  };

  const handleAssignTask = async (suggestion) => {
    try {
      const token = localStorage.getItem('warehouse_token');
      const res = await axios.post(`${API}/housekeeping/create-task`, {
        item_id: suggestion.item?.id,
        quantity: suggestion.quantity,
        from_location_id: suggestion.from_location?.id,
        to_location_id: suggestion.to_location?.id,
        priority: suggestion.priority,
        reason: suggestion.reason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Assigned — slip ${res.data.slip_no} generated`);
      // Auto-print the job sheet so the worker can grab it right away
      printHousekeepingSlip(res.data);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign task');
    }
  };

  const handleCompleteTask = async (task) => {
    if (!window.confirm(`Confirm ${task.item_code} moved from ${task.from_location_code} to ${task.to_location_code}?\n\nSystem will update stock locations.`)) return;
    try {
      const token = localStorage.getItem('warehouse_token');
      const res = await axios.post(`${API}/housekeeping/complete/${task.id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message || 'Housekeeping task completed');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete task');
    }
  };

  const handleCancelTask = async (task) => {
    if (!window.confirm(`Cancel housekeeping slip ${task.slip_no || task.id.slice(0,8)}?`)) return;
    try {
      const token = localStorage.getItem('warehouse_token');
      await axios.delete(`${API}/housekeeping/${task.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Task cancelled');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel');
    }
  };

  if (loading) return <div className="p-8">Loading...</div>;

  const consolidateSuggestions = suggestions.filter(s => s.type === 'consolidate');
  const reviewSuggestions = suggestions.filter(s => s.type === 'review');

  return (
    <div className="p-8" data-testid="housekeeping-page">
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="heading text-3xl font-black text-[#09090B] mb-2" data-testid="housekeeping-title">
            Housekeeping
          </h1>
          <p className="text-[#52525B]">Optimize warehouse space by consolidating stock</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={openMoveModal}
            className="btn-primary flex items-center gap-2"
            data-testid="manual-move-button"
          >
            <Plus size={18} weight="bold" />
            Move Stock
          </button>
          <button
            onClick={fetchSuggestions}
            className="btn-secondary flex items-center gap-2"
            data-testid="refresh-suggestions-button"
          >
            <ArrowsClockwise size={18} weight="bold" />
            Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="card">
          <div className="flex items-center gap-3">
            <Broom size={24} className="text-[#007AFF]" weight="duotone" />
            <div>
              <p className="text-sm text-[#52525B]">Total Suggestions</p>
              <p className="heading text-2xl font-black text-[#09090B]" data-testid="total-suggestions">{suggestions.length}</p>
            </div>
          </div>
        </div>
        <div className="card bg-[#DC2626]/5 border-[#DC2626]/20">
          <div className="flex items-center gap-3">
            <Warning size={24} className="text-[#DC2626]" weight="duotone" />
            <div>
              <p className="text-sm text-[#52525B]">Consolidate</p>
              <p className="heading text-2xl font-black text-[#09090B]" data-testid="consolidate-count">{consolidateSuggestions.length}</p>
            </div>
          </div>
        </div>
        <div className="card bg-[#F59E0B]/5 border-[#F59E0B]/20">
          <div className="flex items-center gap-3">
            <Broom size={24} className="text-[#F59E0B]" weight="duotone" />
            <div>
              <p className="text-sm text-[#52525B]">To Review</p>
              <p className="heading text-2xl font-black text-[#09090B]" data-testid="review-count">{reviewSuggestions.length}</p>
            </div>
          </div>
        </div>
      </div>

      {pendingTasks.length > 0 && (
        <div className="mb-8" data-testid="pending-housekeeping-section">
          <h2 className="heading text-xl font-bold text-[#09090B] mb-4">Assigned Jobs — Waiting for Worker</h2>
          <div className="space-y-3">
            {pendingTasks.map((t) => (
              <div key={t.id} className="card border-[#7C3AED]/30 bg-[#F5F3FF]" data-testid="pending-housekeeping-task">
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <span className="mono text-xs bg-[#7C3AED] text-white px-2 py-1 rounded-sm font-bold" data-testid="hk-slip-no">
                    {t.slip_no || `HK-${t.id.slice(0, 8).toUpperCase()}`}
                  </span>
                  <span className="mono text-sm font-bold text-[#09090B]" data-testid="hk-item-code">{t.item_code || '—'}</span>
                  <span className="text-xs text-[#52525B]">
                    Assigned {new Date(t.created_at).toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="p-2 bg-white rounded border-2 border-[#DC2626] min-w-[110px] text-center">
                    <p className="text-[10px] text-[#52525B] font-bold">FROM</p>
                    <p className="mono text-base font-black text-[#09090B]" data-testid="hk-from">{t.from_location_code || '—'}</p>
                  </div>
                  <ArrowRight size={22} weight="bold" className="text-[#52525B]" />
                  <div className="p-2 bg-white rounded border-2 border-[#16A34A] min-w-[110px] text-center">
                    <p className="text-[10px] text-[#52525B] font-bold">TO</p>
                    <p className="mono text-base font-black text-[#09090B]" data-testid="hk-to">{t.to_location_code || '—'}</p>
                  </div>
                  <div className="ml-2">
                    <p className="text-xs text-[#52525B]">Quantity</p>
                    <p className="mono font-bold text-[#09090B]" data-testid="hk-qty">{t.quantity || 0} plt</p>
                  </div>
                  <div className="ml-auto flex items-center gap-2">
                    <button
                      onClick={() => printHousekeepingSlip({
                        slip_no: t.slip_no,
                        item_code: t.item_code,
                        from_location_code: t.from_location_code,
                        to_location_code: t.to_location_code,
                        quantity: t.quantity,
                        priority: t.priority,
                        reason: t.reason,
                        task_id: t.id,
                        movement_lines: t.movement_lines || [],
                      })}
                      className="btn-secondary text-xs px-3 py-1 inline-flex items-center gap-1"
                      data-testid="hk-reprint-button"
                    >
                      <Printer size={14} weight="bold" />
                      Reprint
                    </button>
                    {(user.role === 'admin' || user.role === 'manager') && (
                      <>
                        <button
                          onClick={() => handleCompleteTask(t)}
                          className="btn-primary text-xs px-3 py-1 inline-flex items-center gap-1"
                          data-testid="hk-complete-button"
                        >
                          <CheckCircle size={14} weight="bold" />
                          Mark Done
                        </button>
                        <button
                          onClick={() => handleCancelTask(t)}
                          className="text-xs px-3 py-1 rounded-md border border-[#DC2626] text-[#DC2626] hover:bg-[#FEF2F2] font-medium"
                          data-testid="hk-cancel-button"
                        >
                          Cancel
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {t.reason && <p className="text-xs text-[#52525B] mt-3">{t.reason}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {suggestions.length === 0 ? (
        <div className="card text-center py-16" data-testid="no-suggestions">
          <CheckCircle size={64} className="mx-auto text-[#16A34A] mb-4" weight="duotone" />
          <h3 className="heading text-2xl font-bold text-[#09090B] mb-2">Warehouse is Optimized</h3>
          <p className="text-[#52525B]">No housekeeping needed. All locations are well utilized.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {consolidateSuggestions.length > 0 && (
            <div>
              <h2 className="heading text-xl font-bold text-[#09090B] mb-4">
                Consolidation Opportunities
              </h2>
              <div className="space-y-3">
                {consolidateSuggestions.map((sug, idx) => (
                  <div
                    key={idx}
                    className={`card ${sug.priority === 'high' ? 'bg-[#DC2626]/5 border-[#DC2626]/30' : 'bg-[#F59E0B]/5 border-[#F59E0B]/30'}`}
                    data-testid="consolidate-suggestion"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <span className={`px-2 py-1 text-xs font-bold rounded-sm ${
                            sug.priority === 'high' ? 'bg-[#DC2626] text-white' : 'bg-[#F59E0B] text-white'
                          }`} data-testid="suggestion-priority">
                            {sug.priority.toUpperCase()} PRIORITY
                          </span>
                          <span className="mono text-sm font-bold text-[#09090B]" data-testid="suggestion-sku">
                            {sug.item?.sku}
                          </span>
                          <span className="text-sm text-[#52525B]">{sug.item?.name}</span>
                        </div>

                        <div className="flex items-center gap-4 my-4">
                          <div className="flex-1 p-3 bg-white rounded border-2 border-[#DC2626]">
                            <p className="text-xs text-[#52525B]">FROM</p>
                            <p className="mono text-lg font-black text-[#09090B]" data-testid="from-location">
                              {sug.from_location?.code}
                            </p>
                            <p className="text-xs text-[#52525B] mt-1">
                              <span className="mono font-bold">{sug.quantity}</span> units • {sug.from_utilization}% utilized
                            </p>
                          </div>
                          <ArrowRight size={32} weight="bold" className="text-[#52525B]" />
                          <div className="flex-1 p-3 bg-white rounded border-2 border-[#16A34A]">
                            <p className="text-xs text-[#52525B]">TO</p>
                            <p className="mono text-lg font-black text-[#09090B]" data-testid="to-location">
                              {sug.to_location?.code}
                            </p>
                            <p className="text-xs text-[#52525B] mt-1">
                              Has same SKU • Room available
                            </p>
                          </div>
                        </div>

                        <p className="text-sm text-[#52525B]" data-testid="suggestion-reason">{sug.reason}</p>
                      </div>

                      {(user.role === 'admin' || user.role === 'manager') && (
                        <button
                          onClick={() => handleAssignTask(sug)}
                          className="btn-primary flex-shrink-0"
                          data-testid="assign-worker-button"
                        >
                          Assign Worker
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {reviewSuggestions.length > 0 && (
            <div>
              <h2 className="heading text-xl font-bold text-[#09090B] mb-4">
                Locations to Review
              </h2>
              <div className="space-y-3">
                {reviewSuggestions.map((sug, idx) => (
                  <div key={idx} className="card" data-testid="review-suggestion">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="px-2 py-1 text-xs font-bold rounded-sm bg-[#52525B] text-white">
                            LOW PRIORITY
                          </span>
                          <span className="mono text-lg font-black text-[#09090B]">
                            {sug.from_location?.code}
                          </span>
                          <span className="text-xs text-[#52525B]">
                            {sug.from_utilization}% utilized
                          </span>
                        </div>
                        <p className="text-sm text-[#52525B]">{sug.reason}</p>
                        {sug.item && (
                          <p className="text-xs mono text-[#09090B] mt-2">
                            Contains: {sug.item.sku} - {sug.item.name}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <Dialog open={showMoveModal} onOpenChange={setShowMoveModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="manual-move-dialog">
          <DialogHeader>
            <DialogTitle className="heading text-xl font-bold">Move Stock — Select Item & Destination</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>FROM Location</Label>
                <Select value={moveFromId} onValueChange={handleFromLocationChange}>
                  <SelectTrigger data-testid="move-from-select"><SelectValue placeholder="Pick source location" /></SelectTrigger>
                  <SelectContent>
                    {locations.filter(l => (l.current_qty || 0) > 0).map(l => (
                      <SelectItem key={l.id} value={l.id}>
                        <span className="mono">{l.code}</span>
                        <span className="text-[#52525B] ml-2 text-xs">({l.current_qty || 0}/{l.capacity} plt)</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>TO Location</Label>
                <Select value={moveToId} onValueChange={setMoveToId}>
                  <SelectTrigger data-testid="move-to-select"><SelectValue placeholder="Pick destination" /></SelectTrigger>
                  <SelectContent>
                    {locations.filter(l => l.id !== moveFromId && (l.capacity || 0) - (l.current_qty || 0) > 0).map(l => (
                      <SelectItem key={l.id} value={l.id}>
                        <span className="mono">{l.code}</span>
                        <span className="text-[#52525B] ml-2 text-xs">({(l.capacity || 0) - (l.current_qty || 0)} plt free)</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {moveFromId && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Stock at Source (tick lines to move)</Label>
                  {moveInventory.length > 0 && (
                    <button
                      onClick={toggleMoveSelectAll}
                      className="text-xs text-[#007AFF] hover:underline"
                      data-testid="move-select-all"
                      type="button"
                    >
                      {moveSelectedIds.size === moveInventory.length ? 'Clear all' : 'Select all'}
                    </button>
                  )}
                </div>
                {moveLoading ? (
                  <div className="py-6 text-center text-[#52525B]">Loading stock…</div>
                ) : moveInventory.length === 0 ? (
                  <div className="py-6 text-center text-[#52525B] border border-dashed border-[#E4E4E7] rounded" data-testid="move-empty">
                    No stock here
                  </div>
                ) : (
                  <div className="border border-[#E4E4E7] rounded-md max-h-[280px] overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-[#F4F4F5] text-xs uppercase text-[#52525B] sticky top-0">
                        <tr>
                          <th className="p-2 w-8"></th>
                          <th className="text-left p-2 font-medium">Item</th>
                          <th className="text-left p-2 font-medium">QC</th>
                          <th className="text-left p-2 font-medium">Batch</th>
                          <th className="text-right p-2 font-medium">Plts</th>
                          <th className="text-right p-2 font-medium">Ctns/Plt</th>
                        </tr>
                      </thead>
                      <tbody>
                        {moveInventory.map((r) => {
                          const checked = moveSelectedIds.has(r.inventory_id);
                          return (
                            <tr
                              key={r.inventory_id}
                              className={`border-t border-[#E4E4E7] cursor-pointer ${checked ? 'bg-[#007AFF]/5' : 'hover:bg-[#F9FAFB]'}`}
                              onClick={() => toggleMoveSelected(r.inventory_id)}
                              data-testid={`move-inv-row-${r.inventory_id}`}
                            >
                              <td className="p-2 text-center">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleMoveSelected(r.inventory_id)}
                                  onClick={(e) => e.stopPropagation()}
                                  data-testid={`move-inv-check-${r.inventory_id}`}
                                />
                              </td>
                              <td className="p-2 mono font-bold">{r.item_code || '—'}</td>
                              <td className="p-2 mono text-[#52525B]">{r.qc_number || '—'}</td>
                              <td className="p-2 mono text-[#52525B]">{r.batch_number || '—'}</td>
                              <td className="p-2 mono text-right font-bold">{r.pallets}</td>
                              <td className="p-2 mono text-right">{r.cartons}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                {moveSelectedIds.size > 0 && (
                  <p className="text-xs text-[#52525B] mt-2" data-testid="move-selected-summary">
                    Selected: <span className="font-bold text-[#09090B]">{moveSelectedIds.size} line(s)</span>,{' '}
                    <span className="font-bold text-[#09090B]">
                      {moveInventory.filter(r => moveSelectedIds.has(r.inventory_id)).reduce((s, r) => s + r.pallets, 0)} plt
                    </span>
                  </p>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Priority</Label>
                <Select value={movePriority} onValueChange={setMovePriority}>
                  <SelectTrigger data-testid="move-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Reason (optional)</Label>
                <Input
                  value={moveReason}
                  onChange={(e) => setMoveReason(e.target.value)}
                  placeholder="e.g., Consolidate to free Zone A slot"
                  data-testid="move-reason-input"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={handleCreateManualMove}
                disabled={moveSubmitting || moveSelectedIds.size === 0 || !moveFromId || !moveToId}
                className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="move-submit-button"
              >
                {moveSubmitting ? 'Creating…' : 'Create & Print Slip'}
              </button>
              <button
                onClick={() => setShowMoveModal(false)}
                className="flex-1 btn-secondary"
                data-testid="move-cancel-button"
              >
                Cancel
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
